import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from 'react-query'

import { toast } from '@webapp/components/Notification'
import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ReportAccessGrant } from '@webapp/platform/iam/entities/ReportAccessGrant'
import { type User } from '@webapp/platform/iam/entities/User'
import { useClearSecondaryCategoryReportAccessPresenter } from '@webapp/platform/iam/features/clear-secondary-category-report-access/presenters/ClearSecondaryCategoryReportAccessPresenter'
import { useFirstPrimaryCategoryPresenter } from '@webapp/platform/iam/features/get-primary-category-list/presenters/FirstPrimaryCategoryPresenter'
import { useGrantReportAccessPresenter } from '@webapp/platform/iam/features/grant-report-access/presenters/GrantReportAccessPresenter'
import { useGrantReportAccessToAllPrimaryCategoriesPresenter } from '@webapp/platform/iam/features/grant-report-access-to-all-primary-categories/presenters/GrantReportAccessToAllPrimaryCategoriesPresenter'
import { useGrantReportAccessToAllSecondaryCategoriesPresenter } from '@webapp/platform/iam/features/grant-report-access-to-all-secondary-categories/presenters/GrantReportAccessToAllSecondaryCategoriesPresenter'
import { useRevokeReportAccessFromAllPrimaryCategoriesPresenter } from '@webapp/platform/iam/features/revoke-report-access-from-all-primary-categories/presenters/RevokeReportAccessFromAllPrimaryCategoriesPresenter'
import { useRevokeReportAccessGrantPresenter } from '@webapp/platform/iam/features/revoke-report-access-grant/presenters/RevokeReportAccessGrantPresenter'
import { reportAccessSecondaryCategoryQueryConfig } from '@webapp/platform/iam/query-configs/reportAccessSecondaryCategory'
import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import {
  type ReportAccessSubmitActionEnum,
  ReportAccessSubmitActions,
} from './enums'
import messages from './messages'

const HIGHLIGHT_DURATION_MS = 1000

export const useReportAccessView = () => {
  const intl = useProcurifyIntl()
  const queryClient = useQueryClient()

  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState<
    PrimaryCategory | undefined
  >(undefined)
  const [isAllPrimaryCategories, setIsAllPrimaryCategories] = useState(false)
  const [selectedSecondaryCategory, setSelectedSecondaryCategory] = useState<
    SecondaryCategoryConfig | undefined
  >(undefined)
  const [isAllSecondaryCategories, setIsAllSecondaryCategories] =
    useState(false)
  const [submitAction, setSubmitAction] =
    useState<ReportAccessSubmitActionEnum>(ReportAccessSubmitActions.ADD)

  const [highlightedGrantId, setHighlightedGrantId] = useState<
    number | undefined
  >(undefined)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const [isClearAllConfirmOpen, setIsClearAllConfirmOpen] = useState(false)
  const [pendingClearAllGrants, setPendingClearAllGrants] = useState<
    ReportAccessGrant[]
  >([])
  const [isBulkRemoveConfirmOpen, setIsBulkRemoveConfirmOpen] = useState(false)
  const [pendingBulkRemoveUser, setPendingBulkRemoveUser] = useState<
    User | undefined
  >(undefined)

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    }
  }, [])

  const highlightGrant = (grantId: number) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    setHighlightedGrantId(grantId)
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedGrantId(undefined)
    }, HIGHLIGHT_DURATION_MS)
  }

  const {
    data: { firstPrimaryCategory },
    states: { isFetching: isFetchingFirstPrimaryCategory },
  } = useFirstPrimaryCategoryPresenter()

  // Init: auto-select the first PrimaryCategory on load (BHV-002)
  useEffect(() => {
    if (
      !selectedPrimaryCategory &&
      !isAllPrimaryCategories &&
      firstPrimaryCategory
    ) {
      setSelectedPrimaryCategory(firstPrimaryCategory)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPrimaryCategory])

  const {
    commands: { grant },
    states: { isGranting },
  } = useGrantReportAccessPresenter()

  const {
    commands: { revoke },
  } = useRevokeReportAccessGrantPresenter({
    // A failed revoke restores the tag — re-highlight it (BHV-018)
    onRevokeError: (restoredGrant) => highlightGrant(restoredGrant.id),
  })

  const {
    commands: { clearAll },
  } = useClearSecondaryCategoryReportAccessPresenter()

  const {
    commands: { grantToAllSecondaryCategories },
    states: { isGranting: isGrantingToAllSecondaryCategories },
  } = useGrantReportAccessToAllSecondaryCategoriesPresenter()

  const {
    commands: { grantToAllPrimaryCategories },
    states: { isGranting: isGrantingToAllPrimaryCategories },
  } = useGrantReportAccessToAllPrimaryCategoriesPresenter()

  const {
    commands: { revokeFromAllPrimaryCategories },
    states: { isRevoking: isBulkRemoving },
  } = useRevokeReportAccessFromAllPrimaryCategoriesPresenter()

  // Mode state machine — real PrimaryCategory selection (BHV-004)
  const handleSelectPrimaryCategory = (primaryCategory: PrimaryCategory) => {
    setSelectedPrimaryCategory(primaryCategory)
    setIsAllPrimaryCategories(false)
    setIsAllSecondaryCategories(false)
    setSelectedSecondaryCategory(undefined)
    setSubmitAction(ReportAccessSubmitActions.ADD)
    setHighlightedGrantId(undefined)
  }

  // Mode state machine — "ALL" option (BHV-005); the left pane unmounts
  const handleSelectAllPrimaryCategories = () => {
    setIsAllPrimaryCategories(true)
    setIsAllSecondaryCategories(false)
    setSubmitAction(ReportAccessSubmitActions.ADD)
  }

  const handleSelectSecondaryCategory = (config: SecondaryCategoryConfig) => {
    setIsAllSecondaryCategories(false)
    setHighlightedGrantId(undefined)
    setSelectedSecondaryCategory(config)
  }

  const handleToggleSelectAllSecondaryCategories = () => {
    setIsAllSecondaryCategories((isAllSelected) => !isAllSelected)
  }

  // Single picker dispatch — one handler, four commands by mode
  const handleSubmitUser = async (user: User) => {
    if (isAllPrimaryCategories) {
      if (submitAction === ReportAccessSubmitActions.REMOVE) {
        setPendingBulkRemoveUser(user)
        setIsBulkRemoveConfirmOpen(true)
        return
      }

      try {
        await grantToAllPrimaryCategories(user) // BHV-023
      } catch {
        // Error toast shown by the feature
      }
      return
    }

    if (isAllSecondaryCategories) {
      if (!selectedPrimaryCategory) return

      try {
        await grantToAllSecondaryCategories(selectedPrimaryCategory, user)

        // Post-bulk reset (BHV-021): clear selection so auto-select-first
        // re-fires, and refresh the SecondaryCategory list (partial key)
        setIsAllSecondaryCategories(false)
        setSelectedSecondaryCategory(undefined)
        queryClient.invalidateQueries(
          reportAccessSecondaryCategoryQueryConfig.list(
            selectedPrimaryCategory.id
          ).queryKey
        )
      } catch {
        // Error toast shown by the feature
      }
      return
    }

    if (!selectedSecondaryCategory) return

    try {
      const createdGrant = await grant(user, selectedSecondaryCategory.id)
      highlightGrant(createdGrant.id) // BHV-013
    } catch {
      // Error toast shown by the feature
    }
  }

  // Immediate optimistic revoke — no confirmation (BHV-017)
  const handleRemoveGrant = (grantToRemove: ReportAccessGrant) => {
    if (!selectedSecondaryCategory) return
    void revoke(grantToRemove, selectedSecondaryCategory.id)
  }

  // Clear all — n=0 is a no-op (BHV-020); n>0 opens the danger dialog (BHV-019)
  const handleClearAllRequest = (grants: ReportAccessGrant[]) => {
    if (grants.length === 0) return
    setPendingClearAllGrants(grants)
    setIsClearAllConfirmOpen(true)
  }

  const closeClearAllConfirm = () => {
    setIsClearAllConfirmOpen(false)
    setPendingClearAllGrants([])
  }

  const handleClearAllConfirm = async (done: () => void) => {
    if (!selectedSecondaryCategory) {
      done()
      closeClearAllConfirm()
      return
    }

    try {
      await clearAll(selectedSecondaryCategory.id)
      done()
      closeClearAllConfirm()
    } catch (err) {
      done()
      closeClearAllConfirm()
      // Toast from the CAUGHT error, never closed-over hook state (ADR-10)
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.errorToastDefaultMessage),
      })
    }
  }

  const closeBulkRemoveConfirm = () => {
    setIsBulkRemoveConfirmOpen(false)
    setPendingBulkRemoveUser(undefined)
  }

  const handleBulkRemoveConfirm = async (done: () => void) => {
    if (!pendingBulkRemoveUser) {
      done()
      closeBulkRemoveConfirm()
      return
    }

    try {
      await revokeFromAllPrimaryCategories(pendingBulkRemoveUser)
      done()
      closeBulkRemoveConfirm()
      setSubmitAction(ReportAccessSubmitActions.ADD) // BHV-025
    } catch (err) {
      done()
      closeBulkRemoveConfirm()
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.errorToastDefaultMessage),
      })
    }
  }

  return {
    states: {
      selectedPrimaryCategory,
      isAllPrimaryCategories,
      selectedSecondaryCategory,
      isAllSecondaryCategories,
      submitAction,
      highlightedGrantId,
      isClearAllConfirmOpen,
      isBulkRemoveConfirmOpen,
      pendingClearAllGrants,
      pendingBulkRemoveUser,
      isFetchingFirstPrimaryCategory,
      isSubmitting:
        isGranting ||
        isGrantingToAllSecondaryCategories ||
        isGrantingToAllPrimaryCategories ||
        isBulkRemoving,
    },
    commands: {
      onSubmitUser: handleSubmitUser,
      onRemoveGrant: handleRemoveGrant,
      onClearAllConfirm: handleClearAllConfirm,
      onBulkRemoveConfirm: handleBulkRemoveConfirm,
    },
    methods: {
      onSelectPrimaryCategory: handleSelectPrimaryCategory,
      onSelectAllPrimaryCategories: handleSelectAllPrimaryCategories,
      onSelectSecondaryCategory: handleSelectSecondaryCategory,
      onToggleSelectAllSecondaryCategories:
        handleToggleSelectAllSecondaryCategories,
      onSubmitActionChange: setSubmitAction,
      onClearAllRequest: handleClearAllRequest,
      onClearAllDismiss: closeClearAllConfirm,
      onBulkRemoveDismiss: closeBulkRemoveConfirm,
    },
  }
}
